import { Observable, Subject, combineLatest, map, startWith } from 'rxjs';

import { isNonNullable } from '@camelot/utils';

import { CamCommunicationsService } from '../../services/communications.service';
import { Communication, CommunicationParticipantExtended } from '../../services/dto/communication';
import { Filter } from '../filters/filters.component';

export const communications$ = (
  service: CamCommunicationsService,
  taskId: string,
  filters$?: Subject<Filter>
): Observable<Communication[]> => {
  const communications$ = service.communicationByTaskId
    .get$(taskId)
    .pipe(map(conversation => conversation.communications ?? []));

  if (!filters$) {
    return communications$;
  }

  return combineLatest([communications$, filters$.pipe(startWith({} as Filter))]).pipe(
    map(([conversation, filters]) => _communicationFilters(conversation, filters))
  );
};

export const participants$ = (
  service: CamCommunicationsService,
  taskId: string,
  contactId$: Observable<string | null>
): Observable<CommunicationParticipantExtended[]> => {
  return combineLatest([
    service.communicationByTaskId.get$(taskId).pipe(map(conversation => conversation.participants ?? [])),
    contactId$.pipe(startWith(null)),
  ]).pipe(
    map(data => ({ participants: data[0], contactId: data[1] })),
    map(({ participants, contactId }) =>
      participants.map<CommunicationParticipantExtended>(participant => ({
        ...participant,
        ...{
          isClient: contactId ? participant.contactId === contactId : false,
        },
      }))
    ),
    map(participants => _uniqueParticipants(participants))
  );
};

const _communicationFilters = (communications: Communication[], filter: Filter) => {
  return communications.filter(communication => {
    if (filter.messageType && !filter.messageType.includes(communication.type)) {
      return false;
    }

    if (filter.participants && filter.participants.length > 0) {
      const person = [
        ...[communication.from].flatMap(p => [p.email, p.userId, p.contactId]),
        ...communication.to.flatMap(p => [p.email, p.userId, p.contactId]),
        ...communication.cc.flatMap(p => [p.email, p.userId, p.contactId]),
      ]
        .filter(isNonNullable)
        .find(id => filter.participants?.includes(id));

      if (!person) {
        return false;
      }
    }
    return true;
  });
};

const _uniqueParticipants = (participants: CommunicationParticipantExtended[]) => {
  return participants.reduce<CommunicationParticipantExtended[]>((acc, p) => {
    const key = (p.email?.toLowerCase() || p.userId || p.contactId || '').trim();
    if (!key) {
      return acc;
    }

    const idx = acc.findIndex(x => (x.email?.toLowerCase() || x.userId || x.contactId || '').trim() === key);

    if (idx === -1) {
      acc.push(p);
    } else {
      acc[idx] = { ...acc[idx], isClient: acc[idx].isClient || p.isClient };
    }
    return acc;
  }, []);
};
